import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, FolderOpen, FileText, UploadCloud, Trash2, Loader2, ShieldCheck, Newspaper, Share2, Copy, Check, CalendarDays, Sparkles, TrendingUp, MessageSquare, HeartPulse, Lightbulb, Play, Bot, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { SEO } from "../components/SEO";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { api, formatApiError } from "../lib/api";

function prettySize(n) {
  if (n > 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

const inputCls = "w-full h-11 px-4 rounded-2xl bg-mist border border-black/10 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";

const PLATFORMS = {
  x: { label: "X (Twitter)", cls: "bg-ink text-white" },
  linkedin: { label: "LinkedIn", cls: "bg-brand/10 text-brand" },
  instagram: { label: "Instagram", cls: "bg-pink-500/10 text-pink-600" },
  facebook: { label: "Facebook", cls: "bg-blue-600/10 text-blue-700" },
};

export default function AdminDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("apps");
  const [apps, setApps] = useState([]);
  const [files, setFiles] = useState([]);  const [docs, setDocs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [docTitle, setDocTitle] = useState("");
  const [docCategory, setDocCategory] = useState("general");
  const [docFile, setDocFile] = useState(null);
  const [post, setPost] = useState({ title_tr: "", title_en: "", excerpt_tr: "", excerpt_en: "", content_tr: "", content_en: "", tag: "", image_url: "" });
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [copied, setCopied] = useState(null);
  const [linkEmail, setLinkEmail] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [linkFile, setLinkFile] = useState(null);
  const [appts, setAppts] = useState([]);
  const [appt, setAppt] = useState({ patient_email: "", kind: "zoom", title: "", date: "", link: "", note: "", amount: "", status: "" });
  const [insights, setInsights] = useState(null);
  const [videoBusy, setVideoBusy] = useState(null);
  const [molt, setMolt] = useState(null);
  const [xpack, setXpack] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/admin/files").then((r) => setFiles(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/admin/social-drafts").then((r) => setDrafts(r.data)).catch(() => {});
    api.get("/admin/appointments").then((r) => setAppts(r.data)).catch(() => {});
    api.get("/admin/tudi-insights").then((r) => setInsights(r.data?.id ? r.data : null)).catch(() => {});
    api.get("/admin/moltbook-report").then((r) => setMolt(r.data)).catch(() => {});
    api.get("/admin/x-package").then((r) => setXpack(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === false) navigate("/giris");
    else if (user && user.role !== "admin") navigate("/panel");
    else if (user) load();
  }, [user, navigate, load]);

  if (!user || user.role !== "admin") return null;

  const setStatus = async (id, status) => {
    await api.patch(`/admin/applications/${id}`, { status }).catch(() => {});
    setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
  };

  const uploadDoc = async () => {
    if (!docFile || !docTitle.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("title", docTitle);
      fd.append("category", docCategory);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("upload_success"));
      setDocFile(null); setDocTitle("");
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const removeDoc = async (id) => {
    await api.delete(`/documents/${id}`).catch(() => {});
    load();
  };

  const savePost = async () => {
    if (!post.title_tr.trim() || !post.content_tr.trim()) {
      toast.error("Başlık (TR) ve içerik (TR) zorunlu");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/posts", post);
      toast.success(t("upload_success"));
      setPost({ title_tr: "", title_en: "", excerpt_tr: "", excerpt_en: "", content_tr: "", content_en: "", tag: "", image_url: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const removePost = async (id) => {
    await api.delete(`/admin/posts/${id}`).catch(() => {});
    load();
  };

  const makeTudiVideo = async (id) => {
    setVideoBusy(id);
    try {
      await api.post(`/admin/posts/${id}/tudi-video`);
      toast.success(t("admin_tudivideo_started"));
      setTimeout(load, 10000);
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setVideoBusy(null);
    }
  };

  const refreshDrafts = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/social-drafts/refresh");
      setDrafts((prev) => [...r.data, ...prev]);
      toast.success(t("social_ready"));
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const copyDraft = async (d) => {
    try {
      await navigator.clipboard.writeText(d.text);
      setCopied(d.id);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error(t("form_error"));
    }
  };

  const linkDoc = async () => {
    if (!linkFile || !linkEmail.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", linkFile);
      fd.append("patient_email", linkEmail);
      fd.append("title", linkTitle);
      await api.post("/admin/patient-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("admin_linkdoc_ok"));
      setLinkFile(null); setLinkTitle(""); setLinkEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const addAppt = async () => {
    if (!appt.patient_email.trim() || !appt.title.trim() || !appt.date) return;
    setBusy(true);
    try {
      await api.post("/admin/appointments", appt);
      toast.success(t("admin_appt_ok"));
      setAppt({ patient_email: "", kind: "zoom", title: "", date: "", link: "", note: "", amount: "", status: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const removeAppt = async (id) => {
    await api.delete(`/admin/appointments/${id}`).catch(() => {});
    load();
  };

  const refreshInsights = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/tudi-insights/refresh");
      setInsights(r.data);
      toast.success(t("ins_ready"));
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const refreshXPackage = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/x-package/refresh");
      setXpack(r.data);
      toast.success(t("xpack_ready"));
    } catch (err) {
      toast.error(formatApiError(err, t("form_error")));
    } finally {
      setBusy(false);
    }
  };

  const tabs = [
    { key: "apps", label: t("admin_apps"), icon: ClipboardList },
    { key: "files", label: t("admin_files"), icon: FolderOpen },
    { key: "docs", label: t("admin_docs"), icon: FileText },
    { key: "posts", label: t("admin_posts"), icon: Newspaper },
    { key: "social", label: t("admin_social"), icon: Share2 },
    { key: "insights", label: t("admin_insights"), icon: Sparkles },
    { key: "moltbook", label: t("admin_moltbook"), icon: Bot },
    { key: "xpack", label: t("admin_xpack"), icon: Megaphone },
  ];

  const statuses = ["new", "in_review", "contacted", "closed"];
  const statusLabel = { new: t("status_new"), in_review: t("status_in_review"), contacted: t("status_contacted"), closed: t("status_closed") };
  const setP = (k) => (e) => setPost({ ...post, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-mist">
      <SEO title="Yönetim Paneli" description="2D Healthcare yönetim paneli" path="/admin" noindex />
      <Navbar />
      <div className="pt-28 pb-24 px-6 max-w-6xl mx-auto" data-testid="admin-dashboard">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-2xl bg-ink text-white flex items-center justify-center"><ShieldCheck size={20} /></div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-ink">Admin</h1>
            <p className="text-xs text-sub">{user.email}</p>
          </div>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold transition-colors ${tab === key ? "bg-ink text-white" : "bg-white text-ink/60 border border-black/10 hover:text-ink"}`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {tab === "apps" && (
          <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
          <div className="bg-white rounded-3xl border border-black/5 overflow-hidden" data-testid="admin-apps-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-sub border-b border-black/5">
                    <th className="px-5 py-4">{t("form_name")}</th>
                    <th className="px-5 py-4">{t("form_email")}</th>
                    <th className="px-5 py-4">{t("form_treatment")}</th>
                    <th className="px-5 py-4">KVKK</th>
                    <th className="px-5 py-4">{t("status_new")}</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.id} data-testid={`admin-app-row-${a.id}`} className="border-b border-black/5 hover:bg-mist/60 align-top">
                      <td className="px-5 py-4 font-semibold text-ink">{a.name}<div className="text-[11px] text-sub font-normal">{a.phone} {a.country && `· ${a.country}`}</div></td>
                      <td className="px-5 py-4 text-ink/70">{a.email}</td>
                      <td className="px-5 py-4 text-ink/70 max-w-[220px]"><div className="font-medium">{a.treatment || "—"}</div><div className="text-[11px] text-sub truncate">{a.message}</div></td>
                      <td className="px-5 py-4">{a.consent_kvkk ? <span className="text-emerald-600 text-xs font-bold">✓</span> : <span className="text-red-500 text-xs font-bold">✗</span>}</td>
                      <td className="px-5 py-4 text-[11px] text-sub">{new Date(a.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4">
                        <select data-testid={`admin-status-${a.id}`} value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} className="h-9 px-3 rounded-full bg-mist border border-black/10 text-xs font-semibold focus:outline-none">
                          {statuses.map((s) => <option key={s} value={s}>{statusLabel[s]}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {apps.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-sub text-sm">{t("portal_empty_apps")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-black/5 p-7" data-testid="admin-appt-form">
            <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><CalendarDays size={19} className="text-brand" /> {t("admin_appt_title")}</h2>
            <input data-testid="appt-email" type="email" value={appt.patient_email} onChange={(e) => setAppt({ ...appt, patient_email: e.target.value })} placeholder={t("admin_linkdoc_email")} className={`mt-5 ${inputCls}`} />
            <input data-testid="appt-title" value={appt.title} onChange={(e) => setAppt({ ...appt, title: e.target.value })} placeholder={t("admin_doc_title")} className={`mt-3 ${inputCls}`} />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <select data-testid="appt-kind" value={appt.kind} onChange={(e) => setAppt({ ...appt, kind: e.target.value })} className={inputCls} aria-label={t("admin_appt_kind")}>
                {["zoom", "visit", "travel", "payment", "dose", "other"].map((k) => <option key={k} value={k}>{t(`appt_${k}`)}</option>)}
              </select>
              <input data-testid="appt-date" type="datetime-local" value={appt.date} onChange={(e) => setAppt({ ...appt, date: e.target.value })} className={inputCls} aria-label={t("admin_appt_date")} />
            </div>
            {appt.kind === "payment" && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <input data-testid="appt-amount" value={appt.amount} onChange={(e) => setAppt({ ...appt, amount: e.target.value })} placeholder={t("admin_appt_amount")} className={inputCls} />
                <select data-testid="appt-status" value={appt.status} onChange={(e) => setAppt({ ...appt, status: e.target.value })} className={inputCls}>
                  <option value="pending">{t("appt_status_pending")}</option>
                  <option value="paid">{t("appt_status_paid")}</option>
                </select>
              </div>
            )}
            {appt.kind === "dose" && (
              <select data-testid="appt-status-dose" value={appt.status} onChange={(e) => setAppt({ ...appt, status: e.target.value })} className={`mt-3 ${inputCls}`}>
                <option value="planned">{t("appt_status_planned")}</option>
                <option value="done">{t("appt_status_done")}</option>
              </select>
            )}
            <input data-testid="appt-link" value={appt.link} onChange={(e) => setAppt({ ...appt, link: e.target.value })} placeholder={t("admin_appt_link")} className={`mt-3 ${inputCls}`} />
            <input data-testid="appt-note" value={appt.note} onChange={(e) => setAppt({ ...appt, note: e.target.value })} placeholder={t("admin_appt_note")} className={`mt-3 ${inputCls}`} />
            <button data-testid="appt-submit" onClick={addAppt} disabled={!appt.patient_email.trim() || !appt.title.trim() || !appt.date || busy} className="mt-5 w-full h-11 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:bg-branddeep disabled:opacity-50 transition-colors">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <CalendarDays size={15} />} {t("admin_appt_btn")}
            </button>
            {appts.length > 0 && (
              <div className="mt-5 pt-4 border-t border-black/5 space-y-2 max-h-56 overflow-y-auto" data-testid="admin-appt-list">
                {appts.map((x) => (
                  <div key={x.id} data-testid={`admin-appt-row-${x.id}`} className="flex items-center gap-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-ink truncate">{x.title}</div>
                      <div className="text-sub">{x.owner_email} · {t(`appt_${x.kind}`)} · {new Date(x.date).toLocaleString()}{x.amount ? ` · ${x.amount}` : ""}{x.status ? ` · ${t(`appt_status_${x.status}`)}` : ""}</div>
                    </div>
                    <button data-testid={`admin-appt-del-${x.id}`} onClick={() => removeAppt(x.id)} className="w-7 h-7 rounded-full hover:bg-red-50 flex items-center justify-center text-sub hover:text-red-500 shrink-0"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        )}

        {tab === "files" && (
          <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
            <div className="bg-white rounded-3xl border border-black/5 p-6 space-y-2.5" data-testid="admin-files-list">
              {files.length === 0 && <p className="text-sm text-sub py-8 text-center">{t("portal_empty_files")}</p>}
              {files.map((f) => (
                <div key={f.id} data-testid={`admin-file-row-${f.id}`} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-mist/60 px-4 py-3">
                  <FileText size={17} className={`shrink-0 ${f.locked ? "text-emerald-600" : "text-brand"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink truncate flex items-center gap-2">{f.original_filename}{f.locked && <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 shrink-0">{t("portal_signed")}</span>}</div>
                    <div className="text-[11px] text-sub">{f.owner_email} · {prettySize(f.size)} · {new Date(f.created_at).toLocaleDateString()} · KVKK {f.consent_kvkk ? "✓" : "✗"} / Sözleşme {f.consent_agreement ? "✓" : "✗"}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-3xl border border-black/5 p-7" data-testid="admin-link-doc">
              <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><ShieldCheck size={19} className="text-brand" /> {t("admin_linkdoc_title")}</h2>
              <input data-testid="link-doc-email" type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder={t("admin_linkdoc_email")} className={`mt-5 ${inputCls}`} />
              <input data-testid="link-doc-title" value={linkTitle} onChange={(e) => setLinkTitle(e.target.value)} placeholder={t("admin_doc_title")} className={`mt-3 ${inputCls}`} />
              <input data-testid="link-doc-file" type="file" onChange={(e) => setLinkFile(e.target.files?.[0] || null)} className="mt-3 w-full text-sm text-ink/70" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
              <button data-testid="link-doc-submit" onClick={linkDoc} disabled={!linkFile || !linkEmail.trim() || busy} className="mt-5 w-full h-11 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:bg-branddeep disabled:opacity-50 transition-colors">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} {t("admin_linkdoc_btn")}
              </button>
            </div>
          </div>
        )}

        {tab === "docs" && (
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            <div className="bg-white rounded-3xl border border-black/5 p-7" data-testid="admin-doc-upload">
              <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><UploadCloud size={19} className="text-brand" /> {t("admin_docs")}</h2>
              <input data-testid="admin-doc-title" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder={t("admin_doc_title")} className={`mt-5 ${inputCls}`} />
              <select data-testid="admin-doc-category" value={docCategory} onChange={(e) => setDocCategory(e.target.value)} className={`mt-3 ${inputCls}`}>
                <option value="general">General</option>
                <option value="consent">Onam Formu</option>
                <option value="kvkk">KVKK</option>
                <option value="agreement">Sözleşme</option>
              </select>
              <input data-testid="admin-doc-file" type="file" onChange={(e) => setDocFile(e.target.files?.[0] || null)} className="mt-3 w-full text-sm text-ink/70" accept=".pdf,.doc,.docx" />
              <button data-testid="admin-doc-submit" onClick={uploadDoc} disabled={!docFile || !docTitle.trim() || busy} className="mt-5 w-full h-11 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:bg-branddeep disabled:opacity-50 transition-colors">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} {t("portal_upload_btn")}
              </button>
            </div>
            <div className="bg-white rounded-3xl border border-black/5 p-7 space-y-2.5" data-testid="admin-docs-list">
              {docs.length === 0 && <p className="text-sm text-sub py-6 text-center">—</p>}
              {docs.map((d) => (
                <div key={d.id} data-testid={`admin-doc-row-${d.id}`} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-mist/60 px-4 py-3">
                  <FileText size={17} className="text-brand shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">{d.title}</div>
                    <div className="text-[11px] text-sub">{d.category} · {prettySize(d.size)}</div>
                  </div>
                  <button data-testid={`admin-doc-delete-${d.id}`} onClick={() => removeDoc(d.id)} className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-sub hover:text-red-500"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "posts" && (
          <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="admin-posts">
            <div className="bg-white rounded-3xl border border-black/5 p-7 space-y-3">
              <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><Newspaper size={19} className="text-brand" /> {t("admin_posts")}</h2>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="post-title-tr" value={post.title_tr} onChange={setP("title_tr")} placeholder="Başlık (TR) *" className={inputCls} />
                <input data-testid="post-title-en" value={post.title_en} onChange={setP("title_en")} placeholder="Title (EN)" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={post.excerpt_tr} onChange={setP("excerpt_tr")} placeholder="Özet (TR)" className={inputCls} />
                <input value={post.excerpt_en} onChange={setP("excerpt_en")} placeholder="Excerpt (EN)" className={inputCls} />
              </div>
              <textarea data-testid="post-content-tr" value={post.content_tr} onChange={setP("content_tr")} placeholder="İçerik (TR) *" rows={5} className="w-full px-4 py-3 rounded-2xl bg-mist border border-black/10 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 resize-none" />
              <textarea value={post.content_en} onChange={setP("content_en")} placeholder="Content (EN)" rows={5} className="w-full px-4 py-3 rounded-2xl bg-mist border border-black/10 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 resize-none" />
              <div className="grid grid-cols-2 gap-3">
                <input value={post.tag} onChange={setP("tag")} placeholder="Etiket (örn. iPSC)" className={inputCls} />
                <input value={post.image_url} onChange={setP("image_url")} placeholder="Görsel URL (örn. /img/t7.jpg)" className={inputCls} />
              </div>
              <button data-testid="post-submit" onClick={savePost} disabled={busy} className="w-full h-11 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 hover:bg-branddeep disabled:opacity-50 transition-colors">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Newspaper size={15} />} Yayınla
              </button>
            </div>
            <div className="bg-white rounded-3xl border border-black/5 p-7 space-y-2.5">
              {posts.length === 0 && <p className="text-sm text-sub py-6 text-center">—</p>}
              {posts.map((p) => (
                <div key={p.id} data-testid={`admin-post-row-${p.id}`} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-mist/60 px-4 py-3">
                  <Newspaper size={17} className="text-brand shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">{p.title_tr}</div>
                    <div className="text-[11px] text-sub">{p.tag} · {new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                  {p.video_url ? (
                    <span data-testid={`admin-post-video-ok-${p.id}`} className="text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 shrink-0">{t("admin_tudivideo_ready")}</span>
                  ) : (
                    <button data-testid={`admin-post-video-${p.id}`} onClick={() => makeTudiVideo(p.id)} disabled={videoBusy === p.id} className="h-8 px-3 rounded-full bg-brand/10 text-brand text-[10px] font-bold uppercase tracking-wider hover:bg-brand hover:text-white disabled:opacity-50 transition-colors shrink-0 flex items-center gap-1.5">
                      {videoBusy === p.id ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} {t("admin_tudivideo")}
                    </button>
                  )}
                  <button data-testid={`admin-post-delete-${p.id}`} onClick={() => removePost(p.id)} className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-sub hover:text-red-500"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "social" && (
          <div className="bg-white rounded-3xl border border-black/5 p-7" data-testid="admin-social">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><Share2 size={19} className="text-brand" /> {t("admin_social")}</h2>
              <button data-testid="social-refresh-btn" onClick={refreshDrafts} disabled={busy} className="h-10 px-5 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center gap-2 hover:bg-branddeep disabled:opacity-50 transition-colors">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {t("social_generate")}
              </button>
            </div>
            <p className="mt-2 text-xs text-sub">{t("social_hint")}</p>
            <div className="mt-5 space-y-2.5">
              {drafts.length === 0 && <p className="text-sm text-sub py-8 text-center" data-testid="social-empty">{t("social_empty")}</p>}
              {drafts.map((d) => (
                <div key={d.id} data-testid={`social-draft-${d.id}`} className="rounded-2xl border border-black/5 bg-mist/60 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${(PLATFORMS[d.platform] || PLATFORMS.x).cls}`}>{(PLATFORMS[d.platform] || { label: d.platform }).label}</span>
                    <button data-testid={`social-copy-${d.id}`} onClick={() => copyDraft(d)} className="flex items-center gap-1.5 text-xs font-semibold text-sub hover:text-brand transition-colors">
                      {copied === d.id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />} {copied === d.id ? t("social_copied") : t("social_copy")}
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-ink/80 whitespace-pre-wrap">{d.text}</p>
                  <div className="mt-1 text-[11px] text-sub">{new Date(d.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "insights" && (
          <div className="bg-white rounded-3xl border border-black/5 p-7" data-testid="admin-insights">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><Sparkles size={19} className="text-brand" /> {t("admin_insights")}</h2>
              <button data-testid="insights-refresh-btn" onClick={refreshInsights} disabled={busy} className="h-10 px-5 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center gap-2 hover:bg-branddeep disabled:opacity-50 transition-colors">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {t("ins_refresh")}
              </button>
            </div>
            {!insights ? (
              <p className="mt-6 text-sm text-sub py-8 text-center" data-testid="insights-empty">{t("ins_empty")}</p>
            ) : (
              <>
                <p className="mt-4 text-sm text-ink/80 leading-relaxed" data-testid="insights-summary">{insights.summary}</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: TrendingUp, title: t("ins_themes"), items: insights.themes, testid: "insights-themes" },
                    { icon: MessageSquare, title: t("ins_questions"), items: insights.frequent_questions, testid: "insights-questions" },
                    { icon: HeartPulse, title: t("ins_concerns"), items: insights.concerns, testid: "insights-concerns" },
                    { icon: Lightbulb, title: t("ins_ideas"), items: insights.content_ideas, testid: "insights-ideas" },
                  ].map((g) => (
                    <div key={g.testid} data-testid={g.testid} className="rounded-2xl border border-black/5 bg-mist/60 p-5">
                      <div className="flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-wider text-brand"><g.icon size={14} /> {g.title}</div>
                      <ul className="mt-3 space-y-1.5">
                        {(g.items || []).map((x, i) => <li key={i} className="text-sm text-ink/80 leading-snug flex gap-2"><span className="text-brand mt-1">•</span>{x}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-[11px] text-sub">{t("ins_updated")}: {new Date(insights.created_at).toLocaleString()} · {insights.sample_size} {t("ins_messages")}</p>
              </>
            )}
          </div>
        )}

        {tab === "moltbook" && (
          <div className="bg-white rounded-3xl border border-black/5 p-7" data-testid="admin-moltbook">
            <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><Bot size={19} className="text-brand" /> {t("admin_moltbook")}</h2>
            {!molt ? (
              <p className="mt-6 text-sm text-sub py-8 text-center" data-testid="moltbook-empty">{t("molt_empty")}</p>
            ) : (
              <>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span data-testid="moltbook-status" className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${molt.status === "claimed" ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}>Moltbook: {molt.status}</span>
                  <span data-testid="moltbook-week-stats" className="text-xs text-sub">{t("molt_week")}: <b className="text-ink">{molt.week?.upvotes ?? 0}</b> {t("molt_upvotes")} · <b className="text-ink">{molt.week?.comments ?? 0}</b> {t("molt_comments")} · <b className="text-ink">{molt.week?.follows ?? 0}</b> {t("molt_follows")}</span>
                </div>
                <h3 className="mt-7 text-xs font-heading font-bold uppercase tracking-wider text-brand">{t("molt_posts_title")}</h3>
                <div className="mt-2 space-y-2" data-testid="moltbook-posts">
                  {(molt.posts || []).length === 0 && <p className="text-sm text-sub">—</p>}
                  {(molt.posts || []).map((p) => (
                    <div key={p.post_id} data-testid={`moltbook-post-${p.post_id}`} className="rounded-2xl border border-black/5 bg-mist/60 px-4 py-3 flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-ink">{p.title}</span>
                      <span className="text-[11px] text-sub shrink-0">{p.at ? new Date(p.at).toLocaleDateString() : ""}</span>
                    </div>
                  ))}
                </div>
                <h3 className="mt-7 text-xs font-heading font-bold uppercase tracking-wider text-brand">{t("molt_comments_title")}</h3>
                <div className="mt-2 space-y-2" data-testid="moltbook-comments">
                  {(molt.comments || []).length === 0 && <p className="text-sm text-sub">—</p>}
                  {(molt.comments || []).map((c) => (
                    <div key={c.comment_id} data-testid={`moltbook-comment-${c.comment_id}`} className="rounded-2xl border border-black/5 bg-mist/60 px-4 py-3">
                      <div className="text-xs font-bold text-brand">{c.author}</div>
                      <p className="mt-1 text-sm text-ink/80">{c.text}</p>
                    </div>
                  ))}
                </div>

                <h3 className="mt-8 text-xs font-heading font-bold uppercase tracking-wider text-brand">{t("aibots_title")}</h3>
                <div className="mt-2" data-testid="aibots-block">
                  {!molt.ai_bots || molt.ai_bots.week_total === 0 ? (
                    <p className="text-sm text-sub" data-testid="aibots-empty">{t("aibots_empty")}</p>
                  ) : (
                    <>
                      <p className="text-sm text-ink/80 mb-3">{t("aibots_total")}: <b>{molt.ai_bots.week_total}</b></p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(molt.ai_bots.by_bot || {}).sort((a, b) => b[1] - a[1]).map(([bot, n]) => (
                          <span key={bot} data-testid={`aibot-${bot}`} className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-brand/10 text-brand">{bot} · {n}</span>
                        ))}
                      </div>
                      <div className="mt-3 space-y-1.5">
                        {(molt.ai_bots.recent || []).slice(0, 8).map((v, i) => (
                          <div key={i} className="flex items-center justify-between gap-3 text-xs text-sub rounded-xl bg-mist/60 px-3 py-2">
                            <span className="font-semibold text-ink">{v.bot}</span>
                            <span className="truncate flex-1">{v.path}</span>
                            <span className="shrink-0">{v.at ? new Date(v.at).toLocaleString() : ""}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <h3 className="mt-8 text-xs font-heading font-bold uppercase tracking-wider text-brand">{t("gsc_title")}</h3>
                <div className="mt-2" data-testid="gsc-block">
                  {!molt.gsc ? (
                    <p className="text-sm text-sub" data-testid="gsc-empty">{t("gsc_empty")}</p>
                  ) : (
                    <>
                      <p className="text-xs text-sub mb-3">{molt.gsc.start_date} → {molt.gsc.end_date} · <b className="text-ink">{molt.gsc.total_clicks}</b> {t("gsc_clicks")} · <b className="text-ink">{molt.gsc.total_impressions}</b> {t("gsc_impressions")}</p>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-sub mb-2">{t("gsc_keywords")}</div>
                          <div className="space-y-1.5">
                            {(molt.gsc.targets || []).map((k) => (
                              <div key={k.keyword} data-testid={`gsc-kw-${k.keyword.replace(/\s/g, "-")}`} className="flex items-center justify-between gap-2 text-xs rounded-xl bg-mist/60 px-3 py-2">
                                <span className="text-ink font-semibold truncate">{k.keyword}</span>
                                <span className="shrink-0 font-bold text-brand">{k.position ?? "—"}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-sub mb-2">{t("gsc_topqueries")}</div>
                          <div className="space-y-1.5">
                            {(molt.gsc.top_queries || []).slice(0, 6).map((q) => (
                              <div key={q.query} className="flex items-center justify-between gap-2 text-xs rounded-xl bg-mist/60 px-3 py-2">
                                <span className="text-ink truncate">{q.query}</span>
                                <span className="shrink-0 text-sub">{q.clicks} tık · {q.impressions} gös.</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "xpack" && (
          <div className="bg-white rounded-3xl border border-black/5 p-7" data-testid="admin-xpack">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-heading text-lg font-bold text-ink flex items-center gap-2"><Megaphone size={19} className="text-brand" /> {t("admin_xpack")}</h2>
              <button data-testid="xpack-refresh-btn" onClick={refreshXPackage} disabled={busy} className="h-10 px-5 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center gap-2 hover:bg-branddeep disabled:opacity-50 transition-colors">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Megaphone size={15} />} {t("xpack_refresh")}
              </button>
            </div>
            <p className="mt-2 text-xs text-sub">{t("xpack_hint")}</p>
            {xpack.length === 0 ? (
              <p className="mt-6 text-sm text-sub py-8 text-center" data-testid="xpack-empty">{t("xpack_empty")}</p>
            ) : (
              [["doctor", t("xpack_doctor")], ["article", t("xpack_articles")]].map(([kind, title]) => (
                <div key={kind} data-testid={`xpack-group-${kind}`}>
                  <h3 className="mt-7 text-xs font-heading font-bold uppercase tracking-wider text-brand">{title}</h3>
                  <div className="mt-2 space-y-2.5">
                    {xpack.filter((x) => x.kind === kind).map((x) => (
                      <div key={x.id} data-testid={`xpack-item-${x.id}`} className="rounded-2xl border border-black/5 bg-mist/60 px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-ink text-white">X</span>
                          <button data-testid={`xpack-copy-${x.id}`} onClick={() => copyDraft(x)} className="flex items-center gap-1.5 text-xs font-semibold text-sub hover:text-brand transition-colors">
                            {copied === x.id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />} {copied === x.id ? t("social_copied") : t("social_copy")}
                          </button>
                        </div>
                        <p className="mt-2 text-sm text-ink/80 whitespace-pre-wrap">{x.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
